import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Modal,
  Popover,
  Select,
  Spin,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  ArrowsLeftRight,
  CaretDown,
  CheckCircle,
  ListChecks,
  Paperclip,
} from "@phosphor-icons/react";
import {
  getMatterWorkItems,
  getWorkItemThread,
  actOnWorkItem,
  completeWorkTask,
  snoozeWorkItem,
  unsnoozeWorkItem,
  logWorkItemRequest,
  logWorkItemStep,
  editWorkItem,
  sendWorkItemBackAStop,
  followWorkItem,
  unfollowWorkItem,
  detachWorkItemDocument,
} from "../../service/workItems";
import AttachDocumentModal from "./AttachDocumentModal";
import ReviewCard, { reviewStanceOf } from "../../pages/Home/MyWork/ReviewCard";
import { isLiveReviewCopy } from "../../utils/reviewCopy";
import useWorkItemNoteDraft from "../../hooks/useWorkItemNoteDraft";
import {
  InboxItemActionButton,
  InboxItemBody,
  InboxItemFooterRow,
  InboxItemMeta,
  InboxItemRow,
  InboxListBody,
  InboxListEmptyIconRing,
  InboxListEmptyState,
  InboxListErrorBanner,
  InboxModalFieldGroup,
  InboxModalLabel,
  InboxModalHint,
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
} from "../AIPane/AIPaneStyled";
import { sanitizeRichHtml } from "../../utils/sanitizeRichHtml";
import {
  WorkConditionChips,
  WorkItemInstruction,
  WorkItemSubjectButton,
  WorkLaneHeader,
  WorkNextLine,
  WorkPanelHint,
  WorkStaleChip,
  WorkStateLine,
  WorkStepButton,
  WorkStepChip,
  WorkStepChips,
  WorkStepPopoverBody,
  WorkStepPopoverFooter,
  WorkThreadAuthor,
  WorkThreadBody,
  WorkThreadEntry,
  WorkThreadList,
  WorkVerbBar,
  WorkVerbButton,
  WorkAskChip,
  WorkRouteLine,
  WorkRouteStop,
  WorkRouteArrow,
  WorkAttachmentList,
  WorkAttachmentRow,
  WorkAttachmentMeta,
  WorkFollowingLine,
} from "./WorkItemsStyled";

/**
 * Matter Work panel — the WHG Docs twin of the new Salesforce matter
 * page's work rail (design contract: whg-salesforce
 * docs/work-items-umbrella.md; backend dms_backend #2049).
 *
 * One matter's outstanding work, in lanes:
 *   - In motion   — work items (the baton). Expand for the instruction
 *                   and thread; verbs are exactly what the Salesforce
 *                   doorway says THIS user may do (return / send back /
 *                   close / comment) — same rules as the tracker.
 *   - This stage's checklist — stage-engine tasks; complete-with-outcome
 *                   where the stage demands one (dependents spawn
 *                   org-side, exactly as if completed there).
 *   - Reminders   — update-call / deadline / chase tasks; plain complete.
 *   - Waiting on outside — open Requests, read-only here.
 *
 * Honest states instead of empty chrome: feature off, matter not
 * migrated, and genuinely-no-work each say so in a sentence.
 *
 * Staleness mirrors the Salesforce rail: amber/red chips from the
 * payload (`staleness` / `staleLabel` / `ageDays`), computed org-side
 * so the two surfaces can never disagree.
 */

const escapeHtml = (value) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const toNoteHtml = (textValue) =>
  String(textValue)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

/** Stored instruction HTML → the plain lines the edit box shows. */
const instructionToText = (html) => {
  if (!html) return "";
  const marked = String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "$&\u2022 ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "$&\n");
  const doc = new DOMParser().parseFromString(marked, "text/html");
  return (doc.body.textContent || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n");
};

const OUTCOME_MENU = [
  { key: "Successful", label: "Successful" },
  { key: "Unsuccessful", label: "Unsuccessful" },
  { key: "Skipped", label: "Skipped" },
];

/* ------------------------------------------------------------------ *
 * Activation — the snooze/blocked/outside/chain build (Matthew, 9/4).
 * `item` here IS the doorway payload directly (not a Home row), so
 * these read `item.activation` / `item.snoozedUntil` etc. straight —
 * the Home-row twins live in `pages/Home/MyWork/plate.js`.
 * ------------------------------------------------------------------ */

const formatMD = (iso) => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("M/D") : "";
};

/** A snoozed item whose date has already passed reads as active —
 * the doorway flips it back itself; this defends the same rule
 * client-side against a stale poll. */
const isSnoozeExpired = (until) => {
  if (!until) return false;
  const parsed = dayjs(until);
  return parsed.isValid() && !parsed.startOf("day").isAfter(dayjs().startOf("day"));
};

const effectiveActivation = (item) => {
  const raw = item.activation || "active";
  if (raw === "snoozed" && isSnoozeExpired(item.snoozedUntil)) return "active";
  return raw;
};

/** The doorway's own red Chase/Overdue chip stays loud — never read
 * as quietly "outside" (Matthew, 9/4). */
const isLoudOutside = (item) => item.staleness === "red";

/** The state line shown beside the meta whenever an item isn't plain
 * "active" (null for loud-outside items — their own chip speaks). */
const stateLineForItem = (item) => {
  const activation = effectiveActivation(item);
  if (activation === "outside") {
    return isLoudOutside(item) ? null : item.outsideLabel || null;
  }
  if (activation === "snoozed") {
    const when = formatMD(item.snoozedUntil);
    return `Snoozed${when ? ` until ${when}` : ""}${
      item.snoozeNote ? ` — ${item.snoozeNote}` : ""
    }`;
  }
  if (activation === "blocked") {
    return `Coming up — after ${
      item.blockedBySubject || "another item"
    } is approved`;
  }
  return null;
};

const DEFAULT_SNOOZE_DAYS = 7;

/** "Snooze…" — a date (default +7 days) and a mandatory note. */
const SnoozeModal = ({ open, itemNumber, onClose, onConfirm, busy }) => {
  const [until, setUntil] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setUntil(dayjs().add(DEFAULT_SNOOZE_DAYS, "day"));
    setNote("");
  }, [open]);

  const canConfirm = until != null && note.trim().length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => canConfirm && onConfirm(until.format("YYYY-MM-DD"), toNoteHtml(note.trim()))}
      okText="Snooze"
      okButtonProps={{ disabled: !canConfirm, loading: busy }}
      title={`Snooze ${itemNumber || "this item"}`}
      destroyOnClose
    >
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="snooze-until">Bring it back on</InboxModalLabel>
        <DatePicker
          id="snooze-until"
          value={until}
          onChange={setUntil}
          disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
          style={{ width: "100%" }}
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="snooze-note">
          Why? (travels with the row)
        </InboxModalLabel>
        <Input.TextArea
          id="snooze-note"
          autoSize={{ minRows: 2, maxRows: 5 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Asked client for the statement, checking back in a week."
          maxLength={2000}
        />
      </InboxModalFieldGroup>
    </Modal>
  );
};

SnoozeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  itemNumber: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

/**
 * "Edit…" — the sender rewords an item they sent (Matthew, 10/27). An
 * untouched instruction goes back exactly as stored, so changing only
 * the date never flattens formatting written in Salesforce.
 */
const EditWorkItemModal = ({ open, item, onClose, onConfirm, busy }) => {
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const original = useMemo(() => instructionToText(item.instruction), [item.instruction]);

  useEffect(() => {
    if (!open) return;
    setSubject(item.subject || "");
    setText(original);
    setDueDate(item.dueDate ? dayjs(item.dueDate) : null);
  }, [open, item.subject, item.dueDate, original]);

  const canConfirm = subject.trim().length > 0 && text.trim().length > 0;

  const confirm = () => {
    if (!canConfirm) return;
    onConfirm({
      subject: subject.trim(),
      instruction: text === original && item.instruction
        ? item.instruction
        : toNoteHtml(text.trim()),
      dueDate: dueDate ? dueDate.format("YYYY-MM-DD") : "",
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={confirm}
      okText="Save changes"
      okButtonProps={{ disabled: !canConfirm, loading: busy }}
      title={`Edit ${item.itemNumber || "this item"}`}
      destroyOnClose
    >
      <InboxModalHint>
        {`${item.ownerName || "Whoever holds it"} gets a notice, and the old wording stays in the thread.`}
      </InboxModalHint>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="edit-subject">Title</InboxModalLabel>
        <Input
          id="edit-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={255}
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="edit-instruction">Instruction</InboxModalLabel>
        <Input.TextArea
          id="edit-instruction"
          autoSize={{ minRows: 3, maxRows: 10 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={8000}
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="edit-due">Due date (optional)</InboxModalLabel>
        <DatePicker
          id="edit-due"
          value={dueDate}
          onChange={setDueDate}
          allowClear
          style={{ width: "100%" }}
        />
      </InboxModalFieldGroup>
    </Modal>
  );
};

EditWorkItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

/** Type → the Request tracker's Request_Type__c label, derived
 * org-side exactly this way (WorkItemRequestService); shown here as
 * information only — never sent on the POST. */
const REQUEST_TYPE_BY_ITEM_TYPE = {
  "Order Exam": "IME / Nexus Opinion",
  "Request Records": "Medical Records",
  "Get Statement": "Lay / Buddy Statement",
};
const requestTypeFor = (typeValue) => REQUEST_TYPE_BY_ITEM_TYPE[typeValue] || "Other";

const CHANNEL_OPTIONS = ["Fax", "Email", "Mail", "Phone", "Portal"].map(
  (c) => ({ value: c, label: c }),
);

/** "I've asked…" — who it's out to, how (channel), and an optional
 * expected-by date. Request type is shown, defaulted from the item's
 * own type — never editable, never sent (the doorway derives it). */
const LogRequestModal = ({ open, item, onClose, onConfirm, busy }) => {
  const [requestedFrom, setRequestedFrom] = useState("");
  const [channel, setChannel] = useState(null);
  const [expectedBy, setExpectedBy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRequestedFrom("");
    setChannel(null);
    setExpectedBy(null);
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() =>
        onConfirm({
          requestedFrom: requestedFrom.trim() || undefined,
          channel: channel || undefined,
          expectedBy: expectedBy ? expectedBy.format("YYYY-MM-DD") : undefined,
        })
      }
      okText="Send"
      okButtonProps={{ loading: busy }}
      title="I’ve asked…"
      destroyOnClose
    >
      <InboxModalHint>
        Request type: {requestTypeFor(item?.typeValue)} — the clock now
        runs against them, not you.
      </InboxModalHint>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="ask-who">Who&rsquo;s this out to?</InboxModalLabel>
        <Input
          id="ask-who"
          value={requestedFrom}
          onChange={(e) => setRequestedFrom(e.target.value)}
          placeholder="e.g. Mednick Associates"
          maxLength={255}
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="ask-channel">How</InboxModalLabel>
        <Select
          id="ask-channel"
          style={{ width: "100%" }}
          options={CHANNEL_OPTIONS}
          value={channel}
          onChange={setChannel}
          allowClear
          placeholder="Fax / Email / Mail / Phone / Portal"
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="ask-expected">Expected by (optional)</InboxModalLabel>
        <DatePicker
          id="ask-expected"
          style={{ width: "100%" }}
          value={expectedBy}
          onChange={setExpectedBy}
          disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
        />
      </InboxModalFieldGroup>
    </Modal>
  );
};

LogRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

/* ------------------------------------------------------------------ *
 * Progress — the "sub-chips" build (Matthew, 9/9): "if she called a
 * client, mark 'called' and she can set a follow up… if she requested
 * a medical exam, check 'requested' and from whom and when. She needs
 * help moving these down the conveyor belt and also keeping track of
 * what's what."
 *
 * The doorway sends, per item: `steps[]` (what has happened, labelled
 * org-side so Salesforce and WHG Docs never disagree), `suggestedSteps`
 * (the type's playbook), `nextStep` (what to do now, in plain words),
 * `followUpOn` / `followUpOverdue`, `missingDocument`, `canLogStep`.
 * The UI adds Follow-up and Other to every playbook.
 * ------------------------------------------------------------------ */

const EXTRA_STEPS = ["Follow-up", "Other"];

/** Steps that stand for a touch with the client — the popover asks
 * only when, and an optional follow-up. */
const CLIENT_TOUCH_STEPS = new Set([
  "Called client",
  "Left voicemail",
  "Emailed client",
  "Texted client",
  "Discussed with client",
]);

const stepChipTone = (step) => {
  if (step.step === "Received" || step.step === "Filed") return "done";
  return null;
};

const followUpLabel = (iso) => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("M/D") : "";
};

/**
 * The little form behind each step pill. Fields vary by step:
 *   - client touch → when; optional follow-up; optional note
 *   - Requested    → who it's out to; how; expected back (= follow-up)
 *   - Filed        → the day it was filed; which court; when to expect
 *                    a response (= follow-up); optional note. The date
 *                    is the deliverable (Matthew, 9/10: "technically
 *                    'filed' and maybe a date") — filing and logging
 *                    are rarely the same day.
 *   - Follow-up    → the follow-up date (required); optional note
 *   - Other        → a note (required); optional follow-up
 */
const StepForm = ({ step, item, busy, onConfirm, onCancel }) => {
  const [occurredOn, setOccurredOn] = useState(dayjs());
  const [counterparty, setCounterparty] = useState("");
  const [channel, setChannel] = useState(null);
  const [followUpOn, setFollowUpOn] = useState(null);
  const [note, setNote] = useState("");

  const isRequested = step === "Requested";
  const isFiled = step === "Filed";
  const isFollowUp = step === "Follow-up";
  const isOther = step === "Other";
  const isTouch = CLIENT_TOUCH_STEPS.has(step);

  const canConfirm =
    !busy &&
    (!isFollowUp || followUpOn != null) &&
    (!isOther || note.trim().length > 0);

  const fieldId = (name) => `step-${item.id}-${name}`;

  return (
    <WorkStepPopoverBody data-testid={`step-form-${step}`}>
      {isRequested && (
        <InboxModalHint style={{ margin: 0 }}>
          Request type: {requestTypeFor(item.typeValue)}. Once logged, the
          clock runs against them, not you.
        </InboxModalHint>
      )}
      {(isTouch || isRequested || isFiled) && (
        <InboxModalFieldGroup style={{ margin: 0 }}>
          <InboxModalLabel htmlFor={fieldId("when")}>
            {isFiled ? "Filed on" : "When"}
          </InboxModalLabel>
          <DatePicker
            id={fieldId("when")}
            value={occurredOn}
            onChange={(d) => setOccurredOn(d || dayjs())}
            allowClear={false}
            disabledDate={(d) => d && d.isAfter(dayjs().endOf("day"))}
            style={{ width: "100%" }}
          />
        </InboxModalFieldGroup>
      )}
      {isFiled && (
        <InboxModalFieldGroup style={{ margin: 0 }}>
          <InboxModalLabel htmlFor={fieldId("who")}>Which court?</InboxModalLabel>
          <Input
            id={fieldId("who")}
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            placeholder="e.g. CAVC"
            maxLength={255}
          />
        </InboxModalFieldGroup>
      )}
      {isRequested && (
        <>
          <InboxModalFieldGroup style={{ margin: 0 }}>
            <InboxModalLabel htmlFor={fieldId("who")}>
              Who&rsquo;s it out to?
            </InboxModalLabel>
            <Input
              id={fieldId("who")}
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder={
                item.typeValue === "Get Statement"
                  ? "e.g. the client, by DocuSign"
                  : "e.g. Mednick Associates"
              }
              maxLength={255}
            />
          </InboxModalFieldGroup>
          <InboxModalFieldGroup style={{ margin: 0 }}>
            <InboxModalLabel htmlFor={fieldId("how")}>How</InboxModalLabel>
            <Select
              id={fieldId("how")}
              style={{ width: "100%" }}
              options={CHANNEL_OPTIONS}
              value={channel}
              onChange={setChannel}
              allowClear
              placeholder="Fax / Email / Mail / Phone / Portal"
            />
          </InboxModalFieldGroup>
        </>
      )}
      <InboxModalFieldGroup style={{ margin: 0 }}>
        <InboxModalLabel htmlFor={fieldId("follow")}>
          {isRequested
            ? "Expected back (optional)"
            : isFiled
              ? "Expect a response by (optional)"
              : isFollowUp
                ? "Follow up on"
                : "Follow up on (optional)"}
        </InboxModalLabel>
        <DatePicker
          id={fieldId("follow")}
          value={followUpOn}
          onChange={setFollowUpOn}
          disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
          style={{ width: "100%" }}
        />
      </InboxModalFieldGroup>
      {(isTouch || isFiled || isFollowUp || isOther) && (
        <InboxModalFieldGroup style={{ margin: 0 }}>
          <InboxModalLabel htmlFor={fieldId("note")}>
            {isOther ? "What happened?" : "Note (optional)"}
          </InboxModalLabel>
          <Input.TextArea
            id={fieldId("note")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              isOther
                ? "e.g. Client says the buddy moved — tracking down a new address."
                : "e.g. He'll sign once his wife reads it."
            }
            maxLength={2000}
          />
        </InboxModalFieldGroup>
      )}
      <WorkStepPopoverFooter>
        <Button size="small" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          loading={busy}
          disabled={!canConfirm}
          onClick={() =>
            onConfirm({
              step,
              occurredOn: occurredOn ? occurredOn.format("YYYY-MM-DD") : undefined,
              counterparty: counterparty.trim() || undefined,
              channel: channel || undefined,
              followUpOn: followUpOn ? followUpOn.format("YYYY-MM-DD") : undefined,
              note: note.trim() ? toNoteHtml(note.trim()) : undefined,
            })
          }
        >
          Log it
        </Button>
      </WorkStepPopoverFooter>
    </WorkStepPopoverBody>
  );
};

StepForm.propTypes = {
  step: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * What has happened on the item, as chips, in time order — plus the
 * follow-up flag and the "no document" flag when they apply. Shown on
 * the collapsed card too: the whole point is seeing the state at a
 * glance, not after a click.
 */
export const ProgressChips = ({ item }) => {
  const steps = Array.isArray(item.steps) ? item.steps : [];
  const showFollowUp = Boolean(item.followUpOn) && item.status !== "Closed";
  if (steps.length === 0 && !showFollowUp && !item.missingDocument) return null;
  return (
    <WorkStepChips data-testid={`progress-chips-${item.itemNumber}`}>
      {steps.map((step, index) => (
        <WorkStepChip
          key={`${step.step}-${step.createdAt || index}`}
          $tone={stepChipTone(step)}
          title={step.author ? `${step.author}` : undefined}
        >
          {step.label || step.step}
        </WorkStepChip>
      ))}
      {showFollowUp && (
        <WorkStepChip
          $tone={item.followUpOverdue ? "over" : "flag"}
          data-testid={`follow-up-chip-${item.itemNumber}`}
        >
          {item.followUpOverdue ? "Follow-up due" : "Follow up"}{" "}
          {followUpLabel(item.followUpOn)}
        </WorkStepChip>
      )}
      {item.missingDocument && (
        <WorkStepChip $tone="over" data-testid={`no-document-chip-${item.itemNumber}`}>
          No document attached
        </WorkStepChip>
      )}
    </WorkStepChips>
  );
};

ProgressChips.propTypes = { item: PropTypes.object.isRequired };

/** The pills that log the next step. Only when the doorway says this
 * user may (`canLogStep` — holder or requester of an open item). */
const StepPills = ({ item, onLogged }) => {
  const [openStep, setOpenStep] = useState(null);
  const [busy, setBusy] = useState(false);

  const suggested = Array.isArray(item.suggestedSteps) ? item.suggestedSteps : [];
  const pills = [...suggested, ...EXTRA_STEPS.filter((s) => !suggested.includes(s))];

  const confirm = useCallback(
    (fields) => {
      setBusy(true);
      logWorkItemStep(item.id, fields)
        .then((response) => {
          const said = response?.data?.stepLabel || fields.step;
          message.success(`${said} — logged on ${item.itemNumber}.`);
          setOpenStep(null);
          onLogged();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't log that. Please try again."
          );
        })
        .finally(() => setBusy(false));
    },
    [item, onLogged]
  );

  return (
    <WorkStepChips data-testid={`step-pills-${item.itemNumber}`}>
      <InboxItemMeta style={{ marginRight: 4 }}>Mark progress:</InboxItemMeta>
      {pills.map((step) => (
        <Popover
          key={step}
          trigger="click"
          placement="bottomLeft"
          destroyTooltipOnHide
          open={openStep === step}
          onOpenChange={(next) => setOpenStep(next ? step : null)}
          content={
            <StepForm
              step={step}
              item={item}
              busy={busy}
              onConfirm={confirm}
              onCancel={() => setOpenStep(null)}
            />
          }
        >
          <WorkStepButton type="button" disabled={busy && openStep !== step}>
            {step === "Follow-up" || step === "Other" ? `${step}…` : step}
          </WorkStepButton>
        </Popover>
      ))}
    </WorkStepChips>
  );
};

StepPills.propTypes = {
  item: PropTypes.object.isRequired,
  onLogged: PropTypes.func.isRequired,
};

/**
 * One work item, expandable: instruction, thread, and exactly the verbs
 * the doorway says this user has. Exported for reuse by the Home "My
 * Work" lanes (`matterLabel` prepends the matter to the meta line) and
 * the inbox verdict bar (`defaultExpanded` opens the card with verbs in
 * hand; `closeLabel` lets Close speak evidence — "Accept & close").
 *
 * The copy of a live document review renders as the review's own card
 * instead — one piece of work, one set of buttons (Matthew, 10/27; see
 * utils/reviewCopy).
 */
/**
 * Work Items v2 (Matthew, 11/2): the route under the subject —
 * "Anne ✓ → Heather · sign (now) → Brittany". Nothing for a plain
 * one-person item.
 */
export const RouteLine = ({ item }) => {
  const stops = Array.isArray(item.route) ? item.route : [];
  if (stops.length === 0) return null;
  return (
    <WorkRouteLine data-testid={`route-${item.itemNumber}`} title="The route this item travels">
      {stops.map((hop, index) => {
        const first = (hop.assigneeName || "?").split(" ")[0];
        const ask =
          hop.ask === "Approve" ? " · sign" : hop.ask === "Review" ? " · review" : "";
        return (
          <React.Fragment key={hop.id || `${item.id}-${index}`}>
            {index > 0 && <WorkRouteArrow aria-hidden="true">→</WorkRouteArrow>}
            <WorkRouteStop $state={hop.state} title={`${hop.assigneeName || "?"} — ${hop.askLabel || ""}`}>
              {first}
              {ask}
              {hop.state === "done" ? " ✓" : hop.state === "current" ? " (now)" : ""}
            </WorkRouteStop>
          </React.Fragment>
        );
      })}
      {item.routePosition ? <span>· {item.routePosition}</span> : null}
    </WorkRouteLine>
  );
};

RouteLine.propTypes = { item: PropTypes.object.isRequired };

const askChipFor = (item) =>
  item.ask === "Approve" ? "Sign" : item.ask === "Review" ? "Review" : null;

export const WorkItemCard = ({
  item,
  onActed,
  matterLabel,
  matterId,
  defaultExpanded = false,
  closeLabel = "Close",
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [thread, setThread] = useState(null);
  const [note, setNote] = useWorkItemNoteDraft(item.id);
  const [busyVerb, setBusyVerb] = useState(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Anything that changes the item org-side: refetch the thread on
  // next open and tell the parent to reload its list.
  const afterChange = useCallback(() => {
    setThread(null);
    onActed();
  }, [onActed]);

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!expanded || thread !== null) return;
    getWorkItemThread(item.id)
      .then((response) => setThread(response?.data?.entries || []))
      .catch(() => setThread([]));
  }, [expanded, thread, item.id]);

  const act = useCallback(
    (action, { needsNote }) => {
      const trimmed = note.trim();
      if (needsNote && !trimmed) {
        message.warning("Write a note first — the other side needs your words.");
        return;
      }
      setBusyVerb(action);
      actOnWorkItem(item.id, {
        action,
        ...(trimmed ? { note: toNoteHtml(trimmed) } : {}),
      })
        .then(() => {
          const said = {
            return: item.passToName
              ? `${item.itemNumber} passed on to ${item.passToName}.`
              : `${item.itemNumber} returned to ${
                  item.requestedByName || "the requester"
                }.`,
            sendBack: `${item.itemNumber} sent back to ${
              item.lastAssignedToName || "the assignee"
            }.`,
            sendBackHop: `${item.itemNumber} sent back to ${
              item.previousHopName || "the previous stop"
            }.`,
            follow: `You're following ${item.itemNumber}.`,
            unfollow: `You stopped following ${item.itemNumber}.`,
            close: item.closeNotePrefill
              ? `${item.itemNumber} closed — ${
                  item.requestedByName || "the requester"
                } will see your note.`
              : `${item.itemNumber} closed.`,
            comment: "Comment added.",
          };
          message.success(said[action] || "Done.");
          setNote("");
          setThread(null);
          onActed();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't do that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [item, note, setNote, onActed]
  );

  const handleSnoozeConfirm = useCallback(
    (until, snoozeNote) => {
      setBusyVerb("snooze");
      snoozeWorkItem(item.id, { until, note: snoozeNote })
        .then(() => {
          message.success(
            `${item.itemNumber} snoozed until ${dayjs(until).format("M/D")}.`
          );
          setSnoozeOpen(false);
          onActed();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't snooze that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [item, onActed]
  );

  const handleUnsnooze = useCallback(() => {
    setBusyVerb("unsnooze");
    unsnoozeWorkItem(item.id, {})
      .then(() => {
        message.success(`${item.itemNumber} is active again.`);
        onActed();
      })
      .catch((err) => {
        message.error(
          err?.response?.data?.detail || "Couldn't unsnooze that. Please try again."
        );
      })
      .finally(() => setBusyVerb(null));
  }, [item, onActed]);

  const handleDetach = useCallback(
    (attachment) => {
      setBusyVerb("detach");
      detachWorkItemDocument(item.id, { attachmentId: attachment.id })
        .then(() => {
          message.success(`Removed ${attachment.name || "the document"} from ${item.itemNumber}.`);
          afterChange();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't remove that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [item, afterChange]
  );

  const handleFollowToggle = useCallback(() => {
    const following = Boolean(item.isFollowing);
    setBusyVerb(following ? "unfollow" : "follow");
    (following ? unfollowWorkItem(item.id) : followWorkItem(item.id))
      .then(() => {
        message.success(
          following
            ? `You stopped following ${item.itemNumber}.`
            : `You're following ${item.itemNumber} — you'll hear when it's finished.`
        );
        onActed();
      })
      .catch((err) => {
        message.error(err?.response?.data?.detail || "Couldn't do that. Please try again.");
      })
      .finally(() => setBusyVerb(null));
  }, [item, onActed]);

  const handleSendBackAStop = useCallback(() => {
    const trimmed = note.trim();
    if (!trimmed) {
      message.warning("Say what needs to change — the note travels back with the item.");
      return;
    }
    setBusyVerb("sendBackHop");
    sendWorkItemBackAStop(item.id, { note: toNoteHtml(trimmed) })
      .then(() => {
        message.success(
          `${item.itemNumber} sent back to ${item.previousHopName || "the previous stop"}.`
        );
        setNote("");
        afterChange();
      })
      .catch((err) => {
        message.error(err?.response?.data?.detail || "Couldn't send that back. Please try again.");
      })
      .finally(() => setBusyVerb(null));
  }, [item, note, setNote, afterChange]);

  const handleEditConfirm = useCallback(
    (changes) => {
      setBusyVerb("edit");
      editWorkItem(item.id, changes)
        .then((response) => {
          message.success(
            response?.data?.changed === false
              ? "Nothing changed."
              : `${item.itemNumber} updated — ${
                  item.ownerName || "whoever holds it"
                } has been told.`
          );
          setEditOpen(false);
          afterChange();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't save that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [item, afterChange]
  );

  const handleLogRequestConfirm = useCallback(
    ({ requestedFrom, channel, expectedBy }) => {
      setBusyVerb("logRequest");
      logWorkItemRequest(item.id, { requestedFrom, channel, expectedBy })
        .then(() => {
          message.success(
            `${item.itemNumber} is now out to ${requestedFrom || "the outside world"}.`
          );
          setAskOpen(false);
          onActed();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Couldn't log that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [item, onActed]
  );

  const chip = item.staleness ? (
    <WorkStaleChip $tone={item.staleness}>
      {item.staleLabel || `${item.ageDays}d`}
    </WorkStaleChip>
  ) : null;

  // The copy of a live document review: the review's card, the review's
  // verbs, nothing of the copy's own. Every hook above has already run,
  // so this early return keeps their order stable across renders.
  if (isLiveReviewCopy(item)) {
    const row = item.reviewRow;
    return (
      <ReviewCard
        row={row}
        stance={reviewStanceOf(row, false)}
        matterLabel={matterLabel}
        onActed={onActed}
        onOpenDocument={() => {
          if (row.document?.id) navigate(`/inbox/${row.document.id}`);
          else if (row.matter_id) navigate(`/matter/${row.matter_id}`);
        }}
      />
    );
  }

  // Activation — the snooze/blocked/outside/chain build (Matthew, 9/4).
  const activation = effectiveActivation(item);
  const blocked = activation === "blocked";
  const stateLine = stateLineForItem(item);

  // Progress (Matthew, 9/9). Logging a step and attaching a document
  // are both "holder or requester of an open item" — the doorway's
  // canLogStep says exactly that. Attach also needs to know the
  // matter, for the picker.
  const canLogStep = !blocked && Boolean(item.canLogStep);
  const canAttach = canLogStep && matterId != null;
  // The holder may finish it themselves (Matthew, 9/29); a blank note
  // closes with the org's default in the words of the job.
  const closePrefill = item.closeNotePrefill || null;
  const conditionNames = Array.isArray(item.conditionNames)
    ? item.conditionNames.filter(Boolean)
    : [];

  return (
    <InboxItemRow data-testid={`work-item-${item.itemNumber}`}>
      <WorkItemSubjectButton
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
      >
        {item.subject}
      </WorkItemSubjectButton>
      <InboxItemFooterRow>
        <InboxItemMeta>
          {chip}
          {askChipFor(item) && (
            <WorkAskChip data-testid={`ask-${item.itemNumber}`}>{askChipFor(item)}</WorkAskChip>
          )}
          {matterLabel ? `${matterLabel} · ` : ""}
          {item.courtLabel}
          {item.conditionName ? ` · ${item.conditionName}` : ""}
          {item.attachmentCount > 1 ? ` · ${item.attachmentCount} documents` : ""}
        </InboxItemMeta>
      </InboxItemFooterRow>
      <RouteLine item={item} />
      {stateLine && <WorkStateLine>{stateLine}</WorkStateLine>}
      <ProgressChips item={item} />
      {item.nextStep && !blocked && (
        <WorkNextLine data-testid={`next-step-${item.itemNumber}`}>
          <strong>Next:</strong>
          {item.nextStep}
        </WorkNextLine>
      )}

      {expanded && (
        <>
          {conditionNames.length > 1 && (
            <WorkConditionChips data-testid={`conditions-${item.itemNumber}`}>
              <strong>{conditionNames.length} conditions:</strong>
              {conditionNames.map((name) => (
                <WorkStepChip key={name}>{name}</WorkStepChip>
              ))}
            </WorkConditionChips>
          )}
          {item.instruction && (
            <WorkItemInstruction
              dangerouslySetInnerHTML={{
                __html: sanitizeRichHtml(item.instruction),
              }}
            />
          )}
          {Array.isArray(item.attachments) && item.attachments.length > 0 ? (
            <WorkAttachmentList data-testid={`attachments-${item.itemNumber}`}>
              {item.attachments.map((a, index) => {
                const label = a.name || (a.documentId ? `Document ${a.documentId}` : "Document");
                const meta = [
                  a.role === "Reference" ? "reference" : "what we asked for",
                  a.attachedByName,
                  a.attachedAt ? dayjs(a.attachedAt).format("M/D") : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <WorkAttachmentRow key={a.id || `${item.id}-doc-${index}`}>
                    <span>
                      {/* Stay inside the app: the evidence opens in the
                          inbox viewer, not via a full-page URL hop. */}
                      {a.documentId ? (
                        <WorkItemSubjectButton
                          type="button"
                          onClick={() => navigate(`/inbox/${a.documentId}`)}
                        >
                          {label}
                        </WorkItemSubjectButton>
                      ) : a.url ? (
                        <a href={a.url}>{label}</a>
                      ) : (
                        label
                      )}
                    </span>
                    <WorkAttachmentMeta>
                      {meta}
                      {a.id && canLogStep && (
                        <>
                          {" · "}
                          <WorkItemSubjectButton
                            type="button"
                            disabled={busyVerb != null}
                            onClick={() => handleDetach({ ...a, name: label })}
                            aria-label={`Remove ${label}`}
                          >
                            remove
                          </WorkItemSubjectButton>
                        </>
                      )}
                    </WorkAttachmentMeta>
                  </WorkAttachmentRow>
                );
              })}
            </WorkAttachmentList>
          ) : (
            (item.dmsDocumentId || item.dmsDocumentUrl) && (
              <WorkPanelHint style={{ marginTop: 8 }}>
                {item.dmsDocumentId ? (
                  <WorkItemSubjectButton
                    type="button"
                    onClick={() => navigate(`/inbox/${item.dmsDocumentId}`)}
                  >
                    Open document
                  </WorkItemSubjectButton>
                ) : (
                  <a href={item.dmsDocumentUrl}>Linked document</a>
                )}
              </WorkPanelHint>
            )
          )}
          {(item.canFollow || item.isFollowing || (item.followers || []).length > 0) && (
            <WorkFollowingLine data-testid={`following-${item.itemNumber}`}>
              {(item.followers || []).length > 0 && (
                <span>
                  Following: {(item.followers || []).map((f) => f.name).filter(Boolean).join(", ")}
                </span>
              )}
              {(item.canFollow || item.isFollowing) && (
                <WorkItemSubjectButton
                  type="button"
                  disabled={busyVerb != null}
                  onClick={handleFollowToggle}
                >
                  {item.isFollowing ? "Stop following" : "Follow"}
                </WorkItemSubjectButton>
              )}
            </WorkFollowingLine>
          )}

          {thread === null ? (
            <Spin size="small" style={{ marginTop: 8 }} />
          ) : (
            thread.length > 0 && (
              <WorkThreadList>
                {thread.map((entry, index) => (
                  <WorkThreadEntry key={index}>
                    <WorkThreadAuthor>
                      {entry.author}
                      {entry.whenLabel ? ` · ${entry.whenLabel}` : ""}
                    </WorkThreadAuthor>
                    <WorkThreadBody
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(entry.body),
                      }}
                    />
                  </WorkThreadEntry>
                ))}
              </WorkThreadList>
            )
          )}

          {canLogStep && <StepPills item={item} onLogged={afterChange} />}

          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              closePrefill
                ? `What you did — or leave blank and Close says “${closePrefill}”`
                : item.canReturn
                ? "What you did / what they should know…"
                : "Add a note to the thread…"
            }
            maxLength={4000}
            style={{ marginTop: 8 }}
            aria-label={`Note for ${item.itemNumber}`}
          />
          <WorkVerbBar>
            {canAttach && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => setAttachOpen(true)}
                data-testid={`attach-document-${item.itemNumber}`}
              >
                <Paperclip
                  size={11}
                  weight="bold"
                  style={{ verticalAlign: "-1px", marginRight: 4 }}
                />
                {"Attach document\u2026"}
              </WorkVerbButton>
            )}
            {/* Blocked: no verbs except Comment (Matthew, 9/4) — this
                item is waiting on another one's approval, not on this
                user's action. */}
            {!blocked && item.canReturn && (
              <WorkVerbButton
                type="button"
                $primary
                disabled={busyVerb != null}
                onClick={() => act("return", { needsNote: !item.passToName })}
                data-testid={`return-${item.itemNumber}`}
              >
                {busyVerb === "return"
                  ? item.passToName ? "Passing on…" : "Returning…"
                  : item.passToName
                  ? `Done — pass to ${item.passToName}`
                  : "Return"}
              </WorkVerbButton>
            )}
            {!blocked && item.canSendBackHop && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={handleSendBackAStop}
                data-testid={`send-back-hop-${item.itemNumber}`}
              >
                {busyVerb === "sendBackHop"
                  ? "Sending…"
                  : `Send back to ${item.previousHopName || "previous stop"}`}
              </WorkVerbButton>
            )}
            {!blocked && item.canSendBack && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => act("sendBack", { needsNote: true })}
              >
                {busyVerb === "sendBack"
                  ? "Sending…"
                  : `Send back${
                      item.lastAssignedToName
                        ? ` to ${item.lastAssignedToName}`
                        : ""
                    }`}
              </WorkVerbButton>
            )}
            {!blocked && item.canClose && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => act("close", { needsNote: false })}
              >
                {busyVerb === "close"
                  ? "Closing…"
                  : closePrefill
                  ? "Done — close"
                  : closeLabel}
              </WorkVerbButton>
            )}
            {!blocked && item.canSnooze && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => setSnoozeOpen(true)}
              >
                {"Snooze\u2026"}
              </WorkVerbButton>
            )}
            {!blocked && item.canUnsnooze && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={handleUnsnooze}
              >
                {busyVerb === "unsnooze" ? "Unsnoozing…" : "Unsnooze"}
              </WorkVerbButton>
            )}
            {!blocked && item.canLogRequest && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => setAskOpen(true)}
              >
                {"I\u2019ve asked\u2026"}
              </WorkVerbButton>
            )}
            <WorkVerbButton
              type="button"
              disabled={busyVerb != null}
              onClick={() => act("comment", { needsNote: true })}
            >
              {busyVerb === "comment" ? "Adding…" : "Comment"}
            </WorkVerbButton>
            {item.canEdit && (
              <WorkVerbButton
                type="button"
                disabled={busyVerb != null}
                onClick={() => setEditOpen(true)}
                data-testid={`edit-work-item-${item.itemNumber}`}
              >
                {"Edit\u2026"}
              </WorkVerbButton>
            )}
          </WorkVerbBar>
        </>
      )}

      <SnoozeModal
        open={snoozeOpen}
        itemNumber={item.itemNumber}
        onClose={() => setSnoozeOpen(false)}
        onConfirm={handleSnoozeConfirm}
        busy={busyVerb === "snooze"}
      />
      {item.canEdit && (
        <EditWorkItemModal
          open={editOpen}
          item={item}
          onClose={() => setEditOpen(false)}
          onConfirm={handleEditConfirm}
          busy={busyVerb === "edit"}
        />
      )}
      <LogRequestModal
        open={askOpen}
        item={item}
        onClose={() => setAskOpen(false)}
        onConfirm={handleLogRequestConfirm}
        busy={busyVerb === "logRequest"}
      />
      {canAttach && (
        <AttachDocumentModal
          open={attachOpen}
          item={item}
          matterId={matterId}
          onClose={() => setAttachOpen(false)}
          onAttached={afterChange}
        />
      )}
    </InboxItemRow>
  );
};

WorkItemCard.propTypes = {
  item: PropTypes.object.isRequired,
  onActed: PropTypes.func.isRequired,
  matterLabel: PropTypes.string,
  /** The matter this item sits on — unlocks "Attach document…" (the
   * picker needs to know whose documents to list). */
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Open with instruction/thread/verbs already visible (verdict bar). */
  defaultExpanded: PropTypes.bool,
  /** Evidence language for the requester's Close verb, e.g. "Accept & close". */
  closeLabel: PropTypes.string,
};

const TaskCard = ({ task, onCompleted }) => {
  const [busy, setBusy] = useState(false);

  const complete = useCallback(
    (outcome) => {
      setBusy(true);
      completeWorkTask(task.id, outcome ? { outcome } : {})
        .then(() => {
          message.success(
            outcome ? `Done — ${outcome.toLowerCase()}.` : "Task completed."
          );
          onCompleted();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail ||
              "Couldn't complete the task. Please try again."
          );
        })
        .finally(() => setBusy(false));
    },
    [task.id, onCompleted]
  );

  return (
    <InboxItemRow data-testid={`work-task-${task.id}`}>
      <InboxItemBody>{task.subject}</InboxItemBody>
      <InboxItemFooterRow>
        <InboxItemMeta>
          {task.isOverdue && <WorkStaleChip $tone="red">Overdue</WorkStaleChip>}
          {task.metaLabel}
        </InboxItemMeta>
        {task.needsOutcome ? (
          <Dropdown
            menu={{
              items: OUTCOME_MENU,
              onClick: ({ key }) => complete(key),
            }}
            trigger={["click"]}
            disabled={busy}
          >
            <InboxItemActionButton type="button" disabled={busy}>
              {busy ? "Completing…" : "Complete"}
              <CaretDown size={10} weight="bold" />
            </InboxItemActionButton>
          </Dropdown>
        ) : (
          <InboxItemActionButton
            type="button"
            disabled={busy}
            onClick={() => complete(null)}
          >
            <CheckCircle size={12} weight="bold" />
            {busy ? "Completing…" : "Complete"}
          </InboxItemActionButton>
        )}
      </InboxItemFooterRow>
    </InboxItemRow>
  );
};

TaskCard.propTypes = {
  task: PropTypes.object.isRequired,
  onCompleted: PropTypes.func.isRequired,
};

const MatterWorkPanel = ({ matterId, isActive = false }) => {
  const [loadStatus, setLoadStatus] = useState("idle");
  const [work, setWork] = useState(null);
  // Monotonic request id: a slow response for a previous matter must
  // never overwrite the current one (Bugbot, PR #635 — the user can
  // switch matters faster than Salesforce answers).
  const requestSeq = useRef(0);

  const load = useCallback(() => {
    if (matterId == null) return;
    const seq = ++requestSeq.current;
    setLoadStatus("loading");
    getMatterWorkItems(matterId)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setWork(response?.data || null);
        setLoadStatus("ready");
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setLoadStatus("failed");
      });
  }, [matterId]);

  // Lazy: fetch on first activation, refetch on matter change while
  // active. A verb anywhere refetches the whole panel — one truth.
  useEffect(() => {
    if (!isActive) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, matterId]);

  const workItems = useMemo(() => work?.work_items || [], [work]);
  const checklist = useMemo(() => work?.checklist || [], [work]);
  const reminders = useMemo(() => work?.reminders || [], [work]);
  const waitingOutside = useMemo(() => work?.waiting_outside || [], [work]);

  const empty =
    workItems.length === 0 &&
    checklist.length === 0 &&
    reminders.length === 0 &&
    waitingOutside.length === 0;

  if (loadStatus === "idle" || loadStatus === "loading") {
    return (
      <InboxListBody aria-busy="true">
        <BriefingSkeletonGroup>
          <BriefingSkeletonBlock $w="60%" />
          <BriefingSkeletonBlock $w="90%" />
          <BriefingSkeletonBlock $w="75%" />
        </BriefingSkeletonGroup>
      </InboxListBody>
    );
  }

  if (loadStatus === "failed") {
    return (
      <InboxListBody>
        <InboxListErrorBanner role="alert">
          Couldn't load this matter's work. Switch tabs and back to retry.
        </InboxListErrorBanner>
      </InboxListBody>
    );
  }

  if (work && work.enabled === false) {
    return (
      <InboxListBody>
        <WorkPanelHint>
          Work items aren't switched on in this environment yet.
        </WorkPanelHint>
      </InboxListBody>
    );
  }

  if (work && !work.matter_found) {
    return (
      <InboxListBody>
        <WorkPanelHint>
          This matter hasn't migrated to the new Salesforce yet, so it has
          no Work Items loop. Tasks for it still live in Litify.
        </WorkPanelHint>
      </InboxListBody>
    );
  }

  if (empty) {
    return (
      <InboxListBody>
        <InboxListEmptyState>
          <InboxListEmptyIconRing>
            <ListChecks size={22} weight="bold" />
          </InboxListEmptyIconRing>
          Nothing outstanding on this matter — no work items in motion, no
          checklist tasks, no reminders.
        </InboxListEmptyState>
      </InboxListBody>
    );
  }

  return (
    <InboxListBody data-testid="matter-work-panel">
      {workItems.length > 0 && (
        <>
          <WorkLaneHeader>
            <ArrowsLeftRight
              size={12}
              weight="bold"
              style={{ verticalAlign: "-2px", marginRight: 4 }}
            />
            In motion
          </WorkLaneHeader>
          {workItems.map((item) => (
            <WorkItemCard
              key={item.id}
              item={item}
              matterId={matterId}
              onActed={load}
            />
          ))}
        </>
      )}

      {checklist.length > 0 && (
        <>
          <WorkLaneHeader>This stage's checklist</WorkLaneHeader>
          {checklist.map((task) => (
            <TaskCard key={task.id} task={task} onCompleted={load} />
          ))}
        </>
      )}

      {reminders.length > 0 && (
        <>
          <WorkLaneHeader>Reminders</WorkLaneHeader>
          {reminders.map((task) => (
            <TaskCard key={task.id} task={task} onCompleted={load} />
          ))}
        </>
      )}

      {waitingOutside.length > 0 && (
        <>
          <WorkLaneHeader>Waiting on outside</WorkLaneHeader>
          {waitingOutside.map((row) => (
            <InboxItemRow key={row.id}>
              <InboxItemBody>{row.label}</InboxItemBody>
              <InboxItemFooterRow>
                <InboxItemMeta>
                  {row.isStale && (
                    <WorkStaleChip $tone="red">Stale</WorkStaleChip>
                  )}
                  {row.metaLabel}
                </InboxItemMeta>
              </InboxItemFooterRow>
            </InboxItemRow>
          ))}
        </>
      )}
    </InboxListBody>
  );
};

MatterWorkPanel.propTypes = {
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
};

export default MatterWorkPanel;
